/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { ICoordenadas } from 'modelos/src';
import {
  Subscription,
  debounceTime,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  switchMap,
} from 'rxjs';
import { DialogService } from '../dialog/dialog.service';
import { HelperService } from '../servicios/helper.service';
import { IOsmResult, OsmService } from '../servicios/osm.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ImportarComponent } from 'src/app/entidades/clientes/crear-editar-cliente/importar/importar.component';
import { MapboxService } from '../mapbox/mapbox.service';
import {
  coordenadasToGeoJSONPolygon,
  geoJSONToCoordenadas,
  latLngToLngLat,
} from '../mapbox/mapbox-coords';

/**
 * Dibujo y edición de polígonos (territorios de cliente) sobre Mapbox GL,
 * usando @mapbox/mapbox-gl-draw. Conserva el contrato público que consumen
 * los padres (`coordenadas`, getter `polygons`, `setPoligonos`, `importar`,
 * `eliminar`, `dibujar`, `cancelarDibujo`, búsqueda OSM).
 */
@Component({
  selector: 'app-polygon-draw',
  templateUrl: './polygon-draw.component.html',
  styleUrls: ['./polygon-draw.component.scss'],
  standalone: false,
})
export class PolygonDrawComponent
  implements AfterViewInit, OnDestroy
{
  private helper = inject(HelperService);
  private dalogService = inject(DialogService);
  private matDialog = inject(MatDialog);
  private osm = inject(OsmService);
  private mapboxService = inject(MapboxService);

  public dibujando = true;

  readonly zoom = input<number>(10);
  readonly height = input<string>('500px');
  /** Muestra el buscador de límites administrativos de OpenStreetMap. */
  readonly buscadorOsm = input<boolean>(false);

  @Input() coordenadas: ICoordenadas[][] = [];

  /** Polígonos de otros clientes (solo lectura, referencia). */
  readonly otrosPoligonos = input<ICoordenadas[][]>([]);

  // Búsqueda OSM (Nominatim)
  public busquedaOsm = new FormControl('');
  public resultadosOsm: IOsmResult[] = [];
  public buscandoOsm = false;
  private busqueda$?: Subscription;

  // Mapa
  private readonly cont =
    viewChild.required<ElementRef<HTMLDivElement>>('cont');
  private map?: mapboxgl.Map;
  private draw?: MapboxDraw;
  private cargado = false;
  private resizeObserver?: ResizeObserver;

  private readonly SRC_OTROS = 'otros-poligonos';

  constructor() {
    // Actualiza la capa de "otros polígonos" cuando cambia el input.
    effect(() => {
      const otros = this.otrosPoligonos();
      if (this.cargado) {
        this.renderOtros(otros);
      }
    });
  }

  /** Coordenadas dibujadas actualmente (lo que consumen los padres). */
  get polygons(): ICoordenadas[][] {
    if (!this.draw) {
      return [];
    }
    const fc = this.draw.getAll();
    const response: ICoordenadas[][] = [];
    for (const f of fc.features) {
      if (f.geometry?.type === 'Polygon') {
        const anillos = geoJSONToCoordenadas(f.geometry);
        if (anillos[0]?.length) {
          // geoJSONToCoordenadas conserva el punto de cierre; lo quitamos
          const ring = anillos[0];
          const last = ring[ring.length - 1];
          const first = ring[0];
          if (
            ring.length > 1 &&
            last.lat === first.lat &&
            last.lng === first.lng
          ) {
            ring.pop();
          }
          response.push(ring);
        }
      }
    }
    return response;
  }

  // ===================== Mapa / Draw =====================

  ngAfterViewInit(): void {
    this.map = this.mapboxService.crearMapa(this.cont().nativeElement, {
      zoom: this.zoom(),
    });
    this.draw = new MapboxDraw({
      displayControlsDefault: false,
    });
    this.map.addControl(this.draw as unknown as mapboxgl.IControl);

    this.map.on('load', () => {
      this.cargado = true;
      this.agregarCapaOtros();
      this.renderOtros(this.otrosPoligonos());
      this.cargarCoordenadas();
    });

    // Al terminar de dibujar un polígono, salir del modo dibujo.
    (this.map as any).on('draw.create', () => {
      this.dibujando = false;
    });

    this.resizeObserver = new ResizeObserver(() => this.map?.resize());
    this.resizeObserver.observe(this.cont().nativeElement);

    if (this.buscadorOsm()) {
      this.suscribirBusquedaOsm();
    }
  }

  private cargarCoordenadas(): void {
    if (this.coordenadas?.length) {
      for (const anillo of this.coordenadas) {
        if (anillo?.length >= 3) {
          this.draw?.add(coordenadasToGeoJSONPolygon([anillo]));
        }
      }
      this.encuadrar(this.coordenadas.flat());
      this.dibujando = false;
    } else {
      // Sin territorio previo: arrancar dibujando.
      this.dibujar();
    }
  }

  private agregarCapaOtros(): void {
    if (!this.map || this.map.getSource(this.SRC_OTROS)) {
      return;
    }
    this.map.addSource(this.SRC_OTROS, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    this.map.addLayer({
      id: `${this.SRC_OTROS}-fill`,
      type: 'fill',
      source: this.SRC_OTROS,
      paint: { 'fill-color': '#2e7d32', 'fill-opacity': 0.1 },
    });
    this.map.addLayer({
      id: `${this.SRC_OTROS}-line`,
      type: 'line',
      source: this.SRC_OTROS,
      paint: { 'line-color': '#2e7d32', 'line-width': 1.5, 'line-opacity': 0.6 },
    });
  }

  private renderOtros(otros: ICoordenadas[][]): void {
    const source = this.map?.getSource(this.SRC_OTROS) as
      | mapboxgl.GeoJSONSource
      | undefined;
    if (!source) {
      return;
    }
    source.setData({
      type: 'FeatureCollection',
      features: (otros || [])
        .filter((a) => a?.length >= 3)
        .map((a) => coordenadasToGeoJSONPolygon([a])),
    });
  }

  private encuadrar(puntos: ICoordenadas[]): void {
    if (!this.map || !puntos?.length) {
      return;
    }
    const bounds = new mapboxgl.LngLatBounds();
    puntos.forEach((c) => bounds.extend(latLngToLngLat(c)));
    this.map.fitBounds(bounds, { padding: 40, maxZoom: 14, duration: 0 });
  }

  // ===================== Acciones (FABs) =====================

  public dibujar(): void {
    if (!this.draw) {
      return;
    }
    this.dibujando = true;
    this.draw.changeMode('draw_polygon');
  }

  public cancelarDibujo(): void {
    this.dibujando = false;
    // Salir del modo dibujo (descarta el polígono a medio trazar).
    this.draw?.changeMode('simple_select');
  }

  public async eliminar(): Promise<void> {
    const seleccion = this.draw?.getSelectedIds() ?? [];
    if (!seleccion.length) {
      this.helper.notifWarn('Seleccioná un polígono para eliminar');
      return;
    }
    const confirm = await this.dalogService.confirm(
      'Por favor confirme la acción',
      '¿Eliminar el polígono seleccionado?',
    );
    if (confirm) {
      this.draw?.delete(seleccion as string[]);
      this.helper.notifSuccess('Polígono eliminado');
    }
  }

  /**
   * Reemplaza los polígonos del mapa programáticamente (importación,
   * búsqueda OSM, etc.) y encuadra el mapa.
   */
  public setPoligonos(coordenadas: ICoordenadas[][]): void {
    this.coordenadas = coordenadas;
    if (!this.draw) {
      return;
    }
    this.draw.deleteAll();
    for (const anillo of coordenadas || []) {
      if (anillo?.length >= 3) {
        this.draw.add(coordenadasToGeoJSONPolygon([anillo]));
      }
    }
    this.encuadrar((coordenadas || []).flat());
    this.dibujando = false;
  }

  public async importar(): Promise<void> {
    const config: MatDialogConfig = {
      width: '1200px',
      panelClass: 'dialog-no-padding',
      disableClose: true,
    };

    const res = this.matDialog.open(ImportarComponent, config);
    const data = (await firstValueFrom(res.afterClosed())) as ICoordenadas[][];

    if (data) {
      this.setPoligonos(data);
    }
  }

  // ===================== Búsqueda OSM =====================

  private suscribirBusquedaOsm(): void {
    this.busqueda$ = this.busquedaOsm.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        filter((texto): texto is string => !!texto && texto.length >= 3),
        switchMap((texto) => {
          this.buscandoOsm = true;
          return this.osm.buscar(texto);
        }),
      )
      .subscribe({
        next: (resultados) => {
          this.resultadosOsm = resultados.filter(
            (r) => this.osm.toCoordenadas(r.geojson).length,
          );
          this.buscandoOsm = false;
        },
        error: (err) => {
          console.error(err);
          this.buscandoOsm = false;
          this.helper.notifError('No se pudo consultar OpenStreetMap');
          this.suscribirBusquedaOsm();
        },
      });
  }

  public seleccionarOsm(resultado: IOsmResult): void {
    const coordenadas = this.osm.toCoordenadas(resultado.geojson);
    if (!coordenadas.length) {
      this.helper.notifWarn('El resultado no tiene un polígono asociado');
      return;
    }
    this.setPoligonos(coordenadas);
    this.resultadosOsm = [];
    this.busquedaOsm.setValue('', { emitEvent: false });
    this.helper.notifSuccess(`Territorio cargado: ${resultado.display_name}`);
  }

  ngOnDestroy(): void {
    this.busqueda$?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.map?.remove();
  }
}
